import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { Search, PencilSquare, Trash, PlusCircle, Backspace, Diagram3 } from "react-bootstrap-icons" // Iconos de Bootstrap

import { AlertError, AlertLoading } from '../helpers/AlertHelper'
import { fromNowCapitalize, monthsDiff } from "../helpers/momentHelper"
import formatCurrency from "../helpers/currencyHelper"

import FormModal from "../components/modals/FormModal"
import TableManagement from "../components/tables/TableManagement"

import ProjectsService from "../services/ProjectsService"

import ProjectsForm from "./ProjectsForm"

const Projects = ({ showSearchBar = true }) => {
    const intl = useIntl()
    const [projectId, setProjectId] = useState(null)
    const [projects, setProjects] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("name")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch data
    const fetchProjects = async () => {
        try {
            const response = await ProjectsService.getProjects(currPage, search, perPage, sortBy, sortOrder)
            setProjects(response.data.projects)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchProjects()
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchProjects()
    }, [currPage, perPage, search, sortBy, sortOrder])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="projectsAdd" defaultMessage="Add project" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="projectsEdit" defaultMessage="Edit project" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="projectsDelete" defaultMessage="Delete project" />,
                icon: <Trash />,
            }
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setProjectId(type === 'add' ? false : data._id) // Si 'add', projectid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'name',
            name: <FormattedMessage id="projectsName" defaultMessage="Name" />,
            selector: (row) => row.name,
            sortable: true,
        },
        {
            id: 'reference',
            name: <FormattedMessage id="projectsReference" defaultMessage="Reference" />,
            selector: (row) => row.reference,
            sortable: true,
        },
        {
            id: 'startDate',
            name: <FormattedMessage id="projectsStartDate" defaultMessage="Start date" />,
            selector: (row) => fromNowCapitalize(row.startDate),
            sortable: true,
        },
        {
            id: 'endDate',
            name: <FormattedMessage id="projectsEndDate" defaultMessage="End date" />,
            selector: (row) => fromNowCapitalize(row.endDate),
            sortable: true,
        },
        {
            id: 'duration',
            name: <FormattedMessage id="projectsDuration" defaultMessage="Duration" />,
            selector: (row) => monthsDiff(row.startDate, row.endDate),
            sortable: false,
        },
        {
            id: 'budget',
            name: <FormattedMessage id="projectsBudget" defaultMessage="Budget" />,
            selector: (row) => formatCurrency(row.budget),
            sortable: true,
            right: "true",
        },
        {
            id: 'eligiblePercent',
            name: <FormattedMessage id="projectsEligiblePercent" defaultMessage="Eligible %" />,
            selector: (row) => row.eligiblePercent + '%',
            right: "true",
        },
        {
            id: 'grant',
            name: <FormattedMessage id="projectsGrant" defaultMessage="Grant" />,
            selector: (row) => formatCurrency(row.budget * row.eligiblePercent / 100),
            right: "true",
        }
    ]

    return (
        <Container fluid className="p-0">
            {showSearchBar && (
                <Row className="p-1 bg-secondary">
                    <Col>
                        <InputGroup>
                            <InputGroup.Text><Search /></InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search projects..." })}
                                value={search}
                                onChange={handleSearchChange}
                            />
                            <Button onClick={handleSearchClean} variant="light">
                                <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                            </Button>

                            <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                                <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                            </Button>
                        </InputGroup>
                    </Col>
                </Row>
            )}
            {isLoaded ? (
                <TableManagement
                    icon={<Diagram3 />}
                    title={<FormattedMessage id="navProjects" defaultMessage="Projects" />}
                    columns={columns}
                    data={projects}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={ProjectsForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={projectId}
                action={modalAction} />

        </Container>
    )
}

export default Projects